import { useState, useEffect } from 'react';
import { useFieldContext } from '@/lib/form-context';
import { Field, FieldContent, FieldError, FieldLabel } from '@/components/ui/field';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { type VariantProps } from 'class-variance-authority';
import { schemaFieldVariants } from '../constants';
import { api } from '@/api/client';

export interface SelectOption {
  value: string;
  label: string;
}

interface SelectFieldProps extends VariantProps<typeof schemaFieldVariants> {
  label: string;
  options?: SelectOption[];
  optionsApi?: string;
  optionsApiCallback?: (value: any) => string;
  placeholder?: string;
  readOnly?: boolean;
  loading?: boolean;
  disabled?: boolean;
  required?: boolean;
}

export const SelectField = ({
  label,
  options: staticOptions,
  optionsApi,
  optionsApiCallback,
  placeholder,
  readOnly,
  loading,
  disabled,
  required,
  variant,
}: SelectFieldProps) => {
  const resolvedVariant = variant ?? 'inline';
  const field = useFieldContext<string>();
  const [isOpen, setIsOpen] = useState(false);
  const [fetchedOptions, setFetchedOptions] = useState<SelectOption[]>([]);

  useEffect(() => {
    if (!optionsApi) return;
    api.get(optionsApi).then((res) => {
      const data: unknown[] = Array.isArray(res.data) ? res.data : (res.data?.results ?? []);
      setFetchedOptions(
        data.map((item) => {
          const label = optionsApiCallback ? optionsApiCallback(item) : String(item);
          const value =
            typeof item === 'object' && item !== null && 'id' in item
              ? String((item as any).id)
              : label;
          return { value, label };
        }),
      );
    });
  }, [optionsApi]);

  const options = staticOptions ?? fetchedOptions;
  const errors = field.state.meta.errors;
  const hasError = !isOpen && field.state.meta.isTouched && errors.length > 0;
  const isEmpty = !field.state.value || field.state.value === '';
  const displayErrors = required && isEmpty ? [{ message: `${label} is required` }] : [];

  const selectedLabel = options.find((o) => o.value === field.state.value)?.label;

  return (
    <Field
      orientation={resolvedVariant === 'stacked' ? 'vertical' : 'subgrid'}
      data-invalid={hasError || undefined}
    >
      <FieldLabel htmlFor={field.name}>{label}</FieldLabel>
      <FieldContent>
        {loading ? (
          <Skeleton className="h-8 w-full" />
        ) : readOnly ? (
          <p>{selectedLabel ?? '—'}</p>
        ) : (
          <Select
            value={field.state.value ?? ''}
            disabled={disabled}
            onValueChange={(val) => val !== null && field.handleChange(val)}
            onOpenChange={(open) => {
              setIsOpen(open);
              if (!open) field.handleBlur();
            }}
          >
            <SelectTrigger id={field.name} className="w-full" disabled={disabled}>
              <SelectValue placeholder={placeholder} />
            </SelectTrigger>
            <SelectContent alignItemWithTrigger={false}>
              {options.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
        {hasError && <FieldError errors={displayErrors} />}
      </FieldContent>
    </Field>
  );
};
